import React, { useEffect, useRef, useState } from 'react';
import css from './BookingDetailsForm.module.css';
import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { ExpansionPanelSummary } from '../../../common/components/ExpansionPanelSummary';
import { extraPrice } from '../BookingPaymentForm/extraPrice';
import { getServiceTypeById, SERVICE_TYPE_ID } from '../../models/service-type';
import { SERVICES } from '../../../constants';
import { useDictionary } from '../../../common/contexts/DictionaryContext';
import { isServiceAvailableHelper } from './isServiceAvailableHelper';

const LEGROOM_MAX = 3;

const getTotalPassengers = (transfer) => (transfer.adults || 0) + (transfer.children || 0) + (transfer.kids || 0);

const getServiceTooltip = (code) =>
    [SERVICES.LATE, SERVICES.LATE_TO_MUNICH, SERVICES.LATE_FROM_MUNICH].includes(code) ? 'tooltip.lateBooking' : '';

// late booking service is always disabled as it is calculated automatically
const isServiceDisabled = (code) => [SERVICES.LATE, SERVICES.LATE_TO_MUNICH, SERVICES.LATE_FROM_MUNICH].includes(code);

const isLateBookingServiceEnabled = (transfer, secondTransfer, service) => {
    const isFirstTransferLate = transfer.extras[service];
    const isSecondTransferLate = secondTransfer && secondTransfer.extras[service];

    return Boolean(isFirstTransferLate || isSecondTransferLate);
};

// if a checkbox with service should be checked
const isServiceChecked = (code, transfer, secondTransfer) => {
    if ([SERVICES.LATE, SERVICES.LATE_TO_MUNICH, SERVICES.LATE_FROM_MUNICH].includes(code)) {
        // if late booking service is actual it should be always checked
        return isLateBookingServiceEnabled(transfer, secondTransfer, code);
    }

    return Boolean(transfer.extras[code]);
};

const DEFAULT_SERVICE_UI_RULE = {
    control: 'checkbox',
    sortOrder: 1,
    isAvailable: isServiceAvailableHelper,
    maxQty: () => 1,
};

const SERVICE_UI_RULES = {
    [SERVICES.EXTRA_LEGROOM]: {
        control: 'select',
        sortOrder: 0,
        isAvailable: (_service, transfer) =>
            transfer.serviceTypeId === SERVICE_TYPE_ID.COLLECTIVE && getTotalPassengers(transfer) > 0,
        // Legroom is a per-leg paid seat: cap the options at THIS leg's own passenger count so an
        // asymmetric round trip never offers more seats than the leg has. bookingClean.js repeats
        // this clamp as a final defense at submit.
        maxQty: (transfer) => Math.min(getTotalPassengers(transfer), LEGROOM_MAX),
    },
};

const getServiceUiRule = (code) => SERVICE_UI_RULES[code] || DEFAULT_SERVICE_UI_RULE;

const Services = ({ transfer, secondTransfer, updateTransfer }) => {
    const { t } = useTranslation();
    const { services = [] } = useDictionary();
    const firstRef = useRef(null);
    const [isExpanded, setExpanded] = useState(() => Number(transfer.extras?.[SERVICES.EXTRA_LEGROOM] || 0) > 0);

    const onCheckboxChange = (e, value) => {
        const { name } = e.target;
        //convert to 1/0 for consistency with the orga tool
        updateTransfer({ value: Number(value), name: name });
    };

    const onSelectChange = (e) => {
        const { name, value } = e.target;
        updateTransfer({ value: Number(value), name });
    };

    const isServiceAvailable = (service) => {
        if (service.disabled) {
            return false;
        }

        const rule = getServiceUiRule(service.internalCode);
        return rule.isAvailable(service, transfer, secondTransfer);
    };

    const filteredServices = services
        .filter(isServiceAvailable)
        .slice()
        .sort((a, b) => getServiceUiRule(a.internalCode).sortOrder - getServiceUiRule(b.internalCode).sortOrder);

    const hasLateBookingService = !!filteredServices.find((service) =>
        [SERVICES.LATE, SERVICES.LATE_TO_MUNICH, SERVICES.LATE_FROM_MUNICH].includes(service.internalCode),
    );

    useEffect(() => {
        // Expand so the customer sees the late-booking charge. The service dictionary loads
        // asynchronously, so hasLateBookingService can flip false -> true after mount (refresh /
        // deep-link / slow network); keying the effect on it re-expands once the service appears.
        // It only ever expands, and since the value stays true afterwards it won't fight a manual
        // collapse. (The legroom > 0 case is handled by the useState initializer above: transfer
        // extras are hydrated with the transfer prop synchronously, not fetched with the dictionary.)
        if (hasLateBookingService) {
            setExpanded(true);
        }
    }, [hasLateBookingService]);

    if (!filteredServices.length) {
        return null;
    }

    return (
        <Accordion onChange={(_, expanded) => setExpanded(expanded)} expanded={isExpanded}>
            <ExpansionPanelSummary text="booking.addServices" />
            <Box sx={{ p: 1 }}>
                <div className={css.grid} ref={firstRef}>
                    {filteredServices.map((service) => {
                        const { internalCode } = service;
                        const tooltip = getServiceTooltip(internalCode);
                        const rule = getServiceUiRule(internalCode);
                        const priceLabel = extraPrice(service[getServiceTypeById(transfer.serviceTypeId)]);

                        if (rule.control === 'select') {
                            const maxQty = rule.maxQty(transfer, secondTransfer);
                            const storedValue = Number(transfer.extras[internalCode] || 0);
                            const displayValue = Math.min(storedValue, maxQty);

                            return (
                                <div key={internalCode} className={css.gridCell} style={{ gridColumn: '1 / -1' }}>
                                    <Select
                                        value={displayValue}
                                        name={`extras.${internalCode}`}
                                        onChange={onSelectChange}
                                        inputProps={{
                                            'data-cy': `extras-${internalCode}`,
                                            'aria-label': t(`extras.${internalCode}`, { count: displayValue }),
                                        }}
                                    >
                                        {[...Array(maxQty + 1)].map((_, key) => (
                                            <MenuItem value={key} key={key}>
                                                {key}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <Typography sx={{ position: 'relative', ml: 1 }} noWrap>
                                        {t(`extras.${internalCode}`, { count: displayValue })}&nbsp;{priceLabel}
                                    </Typography>
                                </div>
                            );
                        }

                        return (
                            <FormControlLabel
                                key={internalCode}
                                control={
                                    <Checkbox
                                        checked={isServiceChecked(internalCode, transfer, secondTransfer)}
                                        onChange={onCheckboxChange}
                                        name={`extras.${internalCode}`}
                                        slotProps={{ input: { autoComplete: 'off' } }}
                                        disabled={isServiceDisabled(internalCode)}
                                    />
                                }
                                label={
                                    <Typography sx={{ position: 'relative' }}>
                                        {t(`extras.${internalCode}`)}&nbsp;
                                        {priceLabel}
                                        {tooltip && (
                                            <Tooltip
                                                title={t(tooltip)}
                                                placement="right"
                                                enterTouchDelay={0}
                                                leaveTouchDelay={3000}
                                            >
                                                <HelpOutlineOutlinedIcon
                                                    sx={{
                                                        position: 'absolute',
                                                        p: 0.5,
                                                        top: '-10px',
                                                        right: '-20px',
                                                        fontSize: '16px',
                                                        color: 'text.secondary',
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Typography>
                                }
                            />
                        );
                    })}
                </div>
            </Box>
        </Accordion>
    );
};

export { Services };
